import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { withRouter, RouteComponentProps, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import classnames from "classnames";

import i18n from "../../../i18n";
import { TableSpinner } from "../../../components";
import { BreadCrumb, NoResultFound } from "../../../components/Common";
import { LocalizedProperty, PaginateQuery } from "../../../interfaces";
import { EducationalStageDto } from "../../../interfaces/curriculum";
import { PlanDto } from "../../../interfaces/plan/plan.dto";
import { RoleEnum } from "../../../enums";
import { AccountState } from "../../../store/slices/accountSlice";
import { useGetCourseSelectListQuery } from "../../../store/api/courseApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../../store/api/curriculumApi";
import { useGetEducationalStagesQuery } from "../../../store/api/educationalStageApi";
import {
  useGetPlansQuery,
  useDeletePlanMutation,
  useTogglePlanStateMutation,
} from "../../../store/api/planApi";
import {
  buildSubjectsSearch,
  readSubjectsFiltersFromSearch,
} from "../../../utils/subjectsNavigation";
import DeleteModal from "../../../components/Common/DeleteModal";
import { Plan } from "./Plan";
import { AddEditPlanModal } from "./AddEditPlanModal";
// @ts-ignore
import "../../Subject/SubjectsTabPage.scss";

type TProps = RouteComponentProps & {};

const PlansTabPageComponent: React.FC<TProps> = ({ history, location }) => {
  const { t } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const urlFilters = useMemo(
    () => readSubjectsFiltersFromSearch(location.search),
    [location.search],
  );

  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const selectedEducationSystemId = useMemo(() => {
    if (!educationSystems?.length) return urlFilters.educationSystemId;
    if (
      urlFilters.educationSystemId &&
      educationSystems.some((s: any) => s.id === urlFilters.educationSystemId)
    ) {
      return urlFilters.educationSystemId;
    }
    return educationSystems[0].id;
  }, [educationSystems, urlFilters.educationSystemId]);

  const { data: curricula } = useGetCurriculumSelectListQuery(
    selectedEducationSystemId || undefined,
    { skip: !selectedEducationSystemId },
  );
  const selectedCurriculumId = useMemo(() => {
    if (!curricula?.length) return "";
    if (
      urlFilters.curriculumId &&
      curricula.some((c: any) => c.id === urlFilters.curriculumId)
    ) {
      return urlFilters.curriculumId;
    }
    return curricula[0].id;
  }, [curricula, urlFilters.curriculumId]);

  const { data: allCourses } = useGetCourseSelectListQuery();
  const selectedCourseId = useMemo(() => {
    if (!allCourses?.length) return "";
    if (
      urlFilters.courseId &&
      allCourses.some((c: any) => c.id === urlFilters.courseId)
    ) {
      return urlFilters.courseId;
    }
    return allCourses[0].id;
  }, [allCourses, urlFilters.courseId]);

  const paginateQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = {
      curriculumId: selectedCurriculumId,
      courseId: selectedCourseId,
    };
    return q;
  }, [selectedCurriculumId, selectedCourseId]);

  const {
    currentData: stagesResponse,
    isLoading: loadingStages,
    isFetching: fetchingStages,
  } = useGetEducationalStagesQuery(paginateQuery, {
    skip: !selectedCurriculumId || !selectedCourseId,
  });

  const stages: EducationalStageDto[] = stagesResponse?.data || [];

  const selectedStageId = useMemo(() => {
    if (!stages.length) return "";
    if (urlFilters.stageId) {
      const urlStage = stages.find((s) => s.id === urlFilters.stageId);
      if (urlStage) return urlStage.id;
    }
    if (urlFilters.gradeId) {
      const stageWithGrade = stages.find((s) =>
        s.grades.some((g) => g.id === urlFilters.gradeId),
      );
      if (stageWithGrade) return stageWithGrade.id;
    }
    return stages.find((s) => s.grades.length > 0)?.id || stages[0].id;
  }, [stages, urlFilters.stageId, urlFilters.gradeId]);

  const selectedStage = useMemo(
    () => stages.find((s) => s.id === selectedStageId),
    [stages, selectedStageId],
  );
  const stageGrades = selectedStage?.grades || [];

  const selectedGradeId = useMemo(() => {
    if (!stageGrades.length) return "";
    if (
      urlFilters.gradeId &&
      stageGrades.some((g) => g.id === urlFilters.gradeId)
    ) {
      return urlFilters.gradeId;
    }
    return stageGrades[0].id;
  }, [stageGrades, urlFilters.gradeId]);

  const {
    currentData: dataPlansCurrent,
    isLoading: isLoadingPlans,
    isFetching: isFetchingPlans,
  } = useGetPlansQuery(
    { courseId: selectedCourseId, gradeId: selectedGradeId },
    { skip: !selectedCourseId || !selectedGradeId },
  );
  const dataPlans = dataPlansCurrent ?? [];
  const plansPending =
    Boolean(selectedGradeId) &&
    dataPlansCurrent === undefined &&
    (isLoadingPlans || isFetchingPlans);

  const [deletePlan, { isLoading: isLoadingDelete }] = useDeletePlanMutation();
  const [togglePlanState] = useTogglePlanStateMutation();

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  document.title = `${t("documentTitle", { pageName: "plans" })}`;

  const syncFiltersToUrl = (
    next: {
      educationSystemId?: string;
      curriculumId?: string;
      courseId?: string;
      stageId?: string;
      gradeId?: string;
    },
    mode: "push" | "replace" = "push",
  ) => {
    const search = buildSubjectsSearch({
      educationSystemId: next.educationSystemId ?? selectedEducationSystemId,
      curriculumId: next.curriculumId ?? selectedCurriculumId,
      courseId: next.courseId ?? selectedCourseId,
      stageId: next.stageId ?? selectedStageId,
      gradeId: next.gradeId ?? selectedGradeId,
    });
    if (search === location.search) return;
    if (mode === "push") {
      history.push({ pathname: "/payment/plans", search });
    } else {
      history.replace({ pathname: "/payment/plans", search });
    }
  };

  useEffect(() => {
    if (
      !selectedEducationSystemId ||
      !selectedCurriculumId ||
      !selectedCourseId
    ) {
      return;
    }

    const resolvedStageId =
      selectedStageId ||
      stages.find((s) => s.grades.length > 0)?.id ||
      stages[0]?.id ||
      "";
    const resolvedGradeId =
      selectedGradeId ||
      stages.find((s) => s.id === resolvedStageId)?.grades[0]?.id ||
      "";

    if (
      urlFilters.educationSystemId === selectedEducationSystemId &&
      urlFilters.curriculumId === selectedCurriculumId &&
      urlFilters.courseId === selectedCourseId &&
      urlFilters.stageId === resolvedStageId &&
      urlFilters.gradeId === resolvedGradeId
    ) {
      return;
    }

    syncFiltersToUrl(
      {
        educationSystemId: selectedEducationSystemId,
        curriculumId: selectedCurriculumId,
        courseId: selectedCourseId,
        stageId: resolvedStageId,
        gradeId: resolvedGradeId,
      },
      "replace",
    );
  }, [
    selectedEducationSystemId,
    selectedCurriculumId,
    selectedCourseId,
    selectedStageId,
    selectedGradeId,
    stages,
    urlFilters.educationSystemId,
    urlFilters.curriculumId,
    urlFilters.courseId,
    urlFilters.stageId,
    urlFilters.gradeId,
  ]);

  const onAdd = () => {
    if (!selectedCourseId || !selectedGradeId) return;
    setEditingId(null);
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    if (!selectedCourseId || !selectedGradeId) return;
    setEditingId(id);
    setShowAddEditModal(true);
  };

  const onToggleState = async (id: string) => {
    if (!selectedCourseId || !selectedGradeId) return;
    try {
      await togglePlanState(id).unwrap();
    } catch (err) {
      // error handled by RTK Query
    }
  };

  const onDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deletePlan(deletingId as string).unwrap();
      setDeletingId(null);
      setShowDeleteModal(false);
    } catch (err) {
      // error handled by RTK Query
    }
  };

  const isLoading =
    loadingStages || (fetchingStages && stagesResponse === undefined);

  const renderFilterChip = (
    key: string,
    label: string,
    active: boolean,
    onClick: () => void,
  ) => (
    <button
      key={key}
      type="button"
      className={classnames("subjects-filter-chip", { active })}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const getGradeLabel = (grade: {
    id: string;
    name?: LocalizedProperty;
    value?: LocalizedProperty;
  }) =>
    grade.name?.[langKey] ||
    grade.value?.[langKey] ||
    grade.name?.en ||
    grade.value?.en ||
    "";

  const educationalStagesLink = `/education-system/educational-stages${buildSubjectsSearch(
    {
      educationSystemId: selectedEducationSystemId,
      curriculumId: selectedCurriculumId,
      courseId: selectedCourseId,
    },
  )}`;

  const renderNoStagesEmptyState = () => (
    <Card>
      <CardBody className="text-center text-muted py-5">
        {isSuperAdmin ? (
          <>
            {t("noStagesYetLead")}{" "}
            <Link to={educationalStagesLink} className="fw-medium">
              {t("addStage")}
            </Link>{" "}
            {t("noStagesYetTail")}
          </>
        ) : (
          t("noStagesYet")
        )}
      </CardBody>
    </Card>
  );

  const renderHierarchyFilters = () => (
    <Card className="subjects-hierarchy-filters mb-3">
      <CardBody className="py-3 px-3">
        {stages.length > 0 ? (
          <div className="subjects-filter-row">
            <span className="subjects-filter-label">
              {t("educationalStages")}
            </span>
            <div className="subjects-filter-chips">
              {stages.map((stage) =>
                renderFilterChip(
                  stage.id,
                  stage.name[langKey],
                  selectedStageId === stage.id,
                  () => {
                    syncFiltersToUrl({
                      educationSystemId: selectedEducationSystemId,
                      curriculumId: selectedCurriculumId,
                      courseId: selectedCourseId,
                      stageId: stage.id,
                      gradeId: stage.grades[0]?.id || "",
                    });
                  },
                ),
              )}
            </div>
          </div>
        ) : null}
        {stageGrades.length > 0 ? (
          <div className="subjects-filter-row subjects-filter-row--grades">
            <span className="subjects-filter-label">{t("grade")}</span>
            <div className="subjects-filter-chips">
              {stageGrades.map((grade) =>
                renderFilterChip(
                  grade.id,
                  getGradeLabel(grade),
                  selectedGradeId === grade.id,
                  () => {
                    syncFiltersToUrl({
                      educationSystemId: selectedEducationSystemId,
                      curriculumId: selectedCurriculumId,
                      courseId: selectedCourseId,
                      stageId: selectedStageId,
                      gradeId: grade.id,
                    });
                  },
                ),
              )}
            </div>
            {isSuperAdmin && selectedGradeId ? (
              <div className="subjects-filter-row-actions">
                <Button color="success" size="sm" onClick={onAdd}>
                  <i className="ri-add-fill me-1 align-bottom"></i>
                  {t("addItem", { item: "plan" })}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );

  const renderPlanGrid = () => (
    <Row className="team-list grid-view-filter">
      {plansPending ? (
        <Col>
          <TableSpinner />
        </Col>
      ) : dataPlans.length > 0 ? (
        dataPlans.map((item: PlanDto) => (
          <Col key={item.id} xs={12} sm={6} xl={4} xxl={3} className="mb-3">
            <Plan
              {...item}
              onEdit={() => onEdit(item.id)}
              onDelete={() => onDelete(item.id)}
              onToggleState={() => onToggleState(item.id)}
            />
          </Col>
        ))
      ) : (
        <Col>
          <NoResultFound />
        </Col>
      )}
    </Row>
  );

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("plans")} pageTitle={t("menu")} />

          <Row className="mb-3 align-items-end g-3">
            <Col md={3}>
              <label className="form-label">{t("educationSystem")}</label>
              <Input
                type="select"
                value={selectedEducationSystemId}
                onChange={(e) => {
                  syncFiltersToUrl({
                    educationSystemId: e.target.value,
                    curriculumId: "",
                    courseId: selectedCourseId,
                    stageId: "",
                    gradeId: "",
                  });
                }}
              >
                {(educationSystems || []).map((s: any) => (
                  <option key={s.id} value={s.id}>
                    {s.value?.[langKey] || s.value?.en}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <label className="form-label">{t("curriculumItem")}</label>
              <Input
                type="select"
                value={selectedCurriculumId}
                onChange={(e) => {
                  syncFiltersToUrl({
                    educationSystemId: selectedEducationSystemId,
                    curriculumId: e.target.value,
                    courseId: selectedCourseId,
                    stageId: "",
                    gradeId: "",
                  });
                }}
                disabled={!selectedEducationSystemId}
              >
                {(curricula || []).map((c: any) => (
                  <option key={c.id} value={c.id}>
                    {c.value?.[langKey] || c.value?.en}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          {(allCourses || []).length > 0 ? (
            <Nav tabs className="nav-tabs-custom mb-3">
              {(allCourses || []).map((course: any) => (
                <NavItem key={course.id}>
                  <NavLink
                    style={{ cursor: "pointer" }}
                    className={classnames({
                      active: selectedCourseId === course.id,
                    })}
                    onClick={() => {
                      syncFiltersToUrl({
                        educationSystemId: selectedEducationSystemId,
                        curriculumId: selectedCurriculumId,
                        courseId: course.id,
                        stageId: "",
                        gradeId: "",
                      });
                    }}
                  >
                    {course.value?.[langKey] || course.value?.en}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          ) : null}

          {isLoading ? (
            <TableSpinner />
          ) : !(allCourses || []).length ? (
            <Card>
              <CardBody className="text-center text-muted py-5">
                {t("noResultFound")}
              </CardBody>
            </Card>
          ) : stages.length === 0 ? (
            renderNoStagesEmptyState()
          ) : (
            <>
              {renderHierarchyFilters()}
              {stageGrades.length > 0 ? (
                <Row>
                  <Col xxl={12}>{renderPlanGrid()}</Col>
                </Row>
              ) : (
                <Card>
                  <CardBody className="text-center text-muted py-5">
                    {t("noGradesAssigned")}
                  </CardBody>
                </Card>
              )}
            </>
          )}
        </Container>
      </div>

      {selectedCourseId && selectedGradeId ? (
        <AddEditPlanModal
          courseId={selectedCourseId}
          gradeId={selectedGradeId}
          id={editingId}
          show={showAddEditModal}
          onCloseClick={() => {
            setEditingId(null);
            setShowAddEditModal(false);
          }}
        />
      ) : null}

      <DeleteModal
        loading={isLoadingDelete}
        show={showDeleteModal}
        onDeleteClick={() => onDeleteConfirm()}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
      />
    </>
  );
};

export const PlansTabPage = withRouter(PlansTabPageComponent);
